import React from "react";
import type { UIMessage } from "ai";
import { FormattedMessage } from "react-intl";
import { ToolPart } from "../types";
import { msgs } from "../msgs";
import { Markdown } from "./Markdown";
import { WidgetOutput } from "./WidgetOutput";
import { ApprovalMessage, WidgetApproval } from "./WidgetApproval";
import type { ToolCategory } from "./WidgetApproval";
import { WidgetErrorBoundary } from "./WidgetErrorBoundary";
import { ToolLoading } from "./ToolLoading";

/**
 * Widget cards render at the SAME indentation as the model's text — the column next
 * to the avatar — rather than breaking out to the full chat width. A full-width
 * breakout was tried, but it left the widget's left edge misaligned with the text
 * above it (which starts further right, past the avatar+gap), which read as more
 * jarring than the extra width was worth. `mt-2` adds a bit of breathing room above
 * it, on top of the parent's `space-y-2`, so a widget doesn't read as glued to the
 * sentence introducing it.
 */
function WidgetSpacing({ children }: { children: React.ReactNode }) {
  return <div className="mt-2">{children}</div>;
}

/**
 * What a single tool part renders, split into two groups:
 *
 * - `immediate`: rendered right where the tool part sits in the conversation — the
 *   approval's intro sentence + card/answer. These happen BEFORE anything the model
 *   says afterward, so they render in place, no different from a plain text part.
 * - `deferred`: the actual RESULT (the created/updated object, a table, …). The model
 *   always writes its sentence about the result in a LATER step, so the result must
 *   render AFTER that sentence, not immediately after the tool call — see
 *   AssistantContent below, which holds these back until the next text part.
 */
interface ToolRenderResult {
  immediate: React.ReactNode[];
  deferred: React.ReactNode[];
}

function toolPartRender(
  tc: ToolPart,
  ctx: {
    propertyTicker: string;
    onApproval: (id: string, approved: boolean, category: ToolCategory) => void;
    historical?: boolean;
  }
): ToolRenderResult {
  const { propertyTicker, onApproval, historical } = ctx;
  const none: ToolRenderResult = { immediate: [], deferred: [] };
  // Shown instead of `none` for a tool state that WILL render something but hasn't
  // received the payload it needs yet (approval/output arrive a beat after the state
  // flips). Returning `none` there left an empty avatar row on screen — the dots/loader
  // had already gone, but there was nothing to replace them with until the payload
  // caught up. Keeping the skeleton up bridges that gap with no blank flash.
  const stillLoading: ToolRenderResult = {
    immediate: [<ToolLoading toolType={tc.type} />],
    deferred: [],
  };
  const introMessage = <ApprovalMessage toolType={tc.type} input={tc.input} />;
  const approvalCard = (state: string) => (
    <WidgetSpacing>
      <WidgetApproval
        toolType={tc.type}
        input={tc.input}
        approval={tc.approval!}
        state={state}
        onApproval={onApproval}
        historical={historical}
      />
    </WidgetSpacing>
  );

  switch (tc.state) {
    case "approval-requested":
    case "approval-responded":
      if (!tc.approval) return stillLoading;
      return { immediate: [introMessage, approvalCard(tc.state)], deferred: [] };
    case "output-available": {
      if (!tc.output) return stillLoading;
      const immediate: React.ReactNode[] = [];
      if (tc.approval) {
        // The request + the user's answer happened before the tool ran, so they still
        // render immediately, right here — only the RESULT below is deferred.
        immediate.push(introMessage, approvalCard(tc.state));
      }
      const result = (
        <WidgetSpacing>
          <WidgetOutput
            widget={tc.output.widget}
            content={tc.output.content}
            propertyTicker={propertyTicker}
            entity={tc.output.entity}
          />
        </WidgetSpacing>
      );
      return { immediate, deferred: [result] };
    }
    case "output-denied":
      // Denied tool calls keep the approval card (now showing the user's "No" answer)
      // instead of a bare "Action cancelled" line, so it's still clear afterward what
      // was proposed and rejected — there is no result to defer (the tool never ran).
      // Falls back to the plain message for the (practically unreachable) case of a
      // denial with no approval payload.
      return tc.approval
        ? { immediate: [introMessage, approvalCard(tc.state)], deferred: [] }
        : {
            immediate: [
              <div className="text-xs text-[#9CA3AF] my-1">
                <FormattedMessage {...msgs.actionCancelled} />
              </div>,
            ],
            deferred: [],
          };
    case "output-error":
      // Render nothing: a tool failure must not reveal the tool name/title or any
      // backend context. The user is notified by the assistant's plain text message
      // (the backend forces a text-only step on tool errors).
      return none;
    default:
      return { immediate: [<ToolLoading toolType={tc.type} />], deferred: [] };
  }
}

export interface AssistantContentProps {
  parts: UIMessage["parts"];
  propertyTicker: string;
  onApproval: (id: string, approved: boolean, category: ToolCategory) => void;
  /**
   * True when these parts belong to a message loaded from stored history (a previous
   * session) rather than the live turn. Forwarded to the approval widget so a never-answered
   * pending approval doesn't render actionable Yes/No buttons for a turn that can't be resumed.
   */
  historical?: boolean;
}

export function AssistantContent({
  parts,
  propertyTicker,
  onApproval,
  historical,
}: AssistantContentProps) {
  // A widget render must never be able to blank the whole backoffice page, so each one
  // is isolated in an error boundary. The fallback stays generic on purpose: a tool
  // failure must not reveal the tool name/title or any backend context.
  const fallback = (
    <div className="text-xs text-[#9CA3AF] my-1">
      <FormattedMessage {...msgs.actionCancelled} />
    </div>
  );

  // Rendered top to bottom in `parts`' own order — never reordered — EXCEPT that a
  // tool's RESULT is held in `pending` until the next text part flushes it. That's
  // what makes "Confirma…" → "Respuesta: Sí" → the model's closing sentence → the
  // resulting card/table read in the same order they actually happened, instead of
  // either all-text-first or all-widgets-first regardless of timing.
  const rendered: { key: string; node: React.ReactNode }[] = [];
  let pending: { key: string; node: React.ReactNode }[] = [];

  parts.forEach((part, i) => {
    const type = (part as { type: string }).type;

    if (type === "text") {
      const text = (part as { type: "text"; text: string }).text;
      if (text.trim()) {
        rendered.push({ key: `text-${i}`, node: <Markdown>{text}</Markdown> });
      }
      rendered.push(...pending);
      pending = [];
      return;
    }

    if (type.startsWith("tool-")) {
      const tc = (part as unknown) as ToolPart;
      const { immediate, deferred } = toolPartRender(tc, {
        propertyTicker,
        onApproval,
        historical,
      });
      immediate.forEach((node, j) =>
        rendered.push({
          key: `tool-${i}-now-${j}`,
          node: <WidgetErrorBoundary fallback={fallback}>{node}</WidgetErrorBoundary>,
        })
      );
      deferred.forEach((node, j) =>
        pending.push({
          key: `tool-${i}-later-${j}`,
          node: <WidgetErrorBoundary fallback={fallback}>{node}</WidgetErrorBoundary>,
        })
      );
    }
  });

  // Nothing left to say this pending result after (the tool call was the last thing
  // in the turn) — still show it, just at the end.
  rendered.push(...pending);

  return (
    <div className="space-y-2">
      {rendered.map(({ key, node }) => (
        <React.Fragment key={key}>{node}</React.Fragment>
      ))}
    </div>
  );
}
